/*!

=========================================================
* Light Bootstrap Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/light-bootstrap-dashboard-pro-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, {Component} from "react";
import {API} from "lib/API";
import {Row} from "react-bootstrap";
import LinkCard from "../../../components/Cards/Card/LinkCard";
import PropTypes from "prop-types";
import {capitalizeWords} from "../../../lib/common";
import {withTranslation} from 'react-i18next';
import {Responsive, WidthProvider} from 'react-grid-layout';
import {Redirect} from "react-router-dom";
import ModalDialog from "../../../components/Common/ModalDialog";
import {getSpecificOPErrorMessages} from "../../../lib/notifications/messageUtils";
import {notifyWithMessage} from "../../../lib/notifications/notifyWithMessage";
import {mauiRoutes} from "../../../routes";
import {Helmet} from "react-helmet";

const localStorage = require('../../../lib/localStorage');

const onLayoutChange = () => {
  setTimeout(function(){
    const event = document.createEvent("Event");
    event.initEvent("resize",false,true);
    window.dispatchEvent(event);
    //window.dispatchEvent(new Event('resize'))
  }, 200);
};

const LinkCards = props => {
    const {loaded, groupName, workbooks, icon, trans} = props;
    if(loaded) {
        const ResponsiveGridLayout = WidthProvider(Responsive);

        //Dynamic layout
        const layoutLg = workbooks.map((_, index) => ({
            i: String(index + 1),
            x: (index % 4) * 3, // 4 cards per row
            y: Math.floor(index / 4) * 2, // vertical spacing
            w: 3,
            h: 1.5,
        }));

        const layoutMd = workbooks.map((_, index) => ({
            i: String(index + 1),
            x: (index % 4) * 3,
            y: Math.floor(index / 4) * 2,
            w: 3,
            h: 1.5,
        }));

        const layoutSm = workbooks.map((_, index) => ({
            i: String(index + 1),
            x: (index % 3) * 2,
            y: Math.floor(index / 3) * 2,
            w: 2,
            h: 1.5,
        }));

        const layoutXs = workbooks.map((_, index) => ({
            i: String(index + 1),
            x: (index % 2) * 2,
            y: Math.floor(index / 2) * 2,
            w: 2,
            h: 2,
        }));

        const layoutXxs = workbooks.map((_, index) => ({
            i: String(index + 1),
            x: 0,
            y: index * 2,
            w: 2,
            h: 1.5,
        }));

        const layouts = {
            lg: layoutLg,
            md: layoutMd,
            sm: layoutSm,
            xs: layoutXs,
            xxs: layoutXxs,
        };

        const cards = workbooks.map((row, index) => {
            const footerText = row.description;
            const subHeadingText = row.dualPeriod ? 'dualPeriod' : 'singlePeriod';
            return (
              <div key={index + 1}>
                <LinkCard
                    key={index}
                    icon={{
                        name: icon,
                        status: "neutral"
                    }}
                    subHeading={{
                        text: trans('common:'+subHeadingText),
                        color:"#595959"
                    }}
                    heading={{
                        text: trans('report.name:'+capitalizeWords(row.workbookName)),
                        color: "#23ccef"
                    }}
                    path={{
                      subHeading: capitalizeWords(groupName),
                      heading: row.workbookName
                    }}
                    footerText={trans('kpiCard.footer:'+footerText)}
                />
              </div>
            );
        });
        return (
          <ResponsiveGridLayout className="layout"
                                layouts={layouts}
                                breakpoints={{lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0}}
                                cols={{lg: 12, md: 12, sm: 6, xs: 4, xxs: 2}}
                                isDraggable={false}
                                isResizable={false}
                                containerPadding={[15,0]}
                                onLayoutChange={onLayoutChange}
          >
          {cards}
          </ResponsiveGridLayout>
        );
    }
    return <Row/>;
};

const Category = props => {
    const {loaded, groupName, categories, icon, trans} = props;
    if(loaded) {
        return categories.map((row, index) => {
            const headingId = `category-heading-${index}`;
            return (
                <div key={index}>
                    <h4
                        id={headingId}
                        className="title gridAdjustLeft gridAdjustTopBottom knoa-region"
                        tabIndex={"-1"}
                    >
                        {trans('report.category:' + row.categoryName)}
                    </h4>
                    <div role="group" aria-labelledby={headingId}>
                        <LinkCards
                            loaded={loaded}
                            groupName={groupName}
                            workbooks={row.workbooks}
                            icon={icon}
                            trans={trans}
                        />
                    </div>
                </div>
            );
        });
    }
    return <Row/>;
};

class AllReports extends Component {
    constructor(prop) {
        super(prop);
        const path = this.props.location.pathname.split('/');
        this.mainContent = React.createRef();
        this.state = {
            path : this.props.location.pathname,
            categories: PropTypes.array,
            reportGroup : path[path.length-2],
            loaded: false,
            showLoggedoutModal: false,
            sessionTerminated: false
        };
    }

    handleLogoutConfirm = () =>{
        this.setState({sessionTerminated: true});
    };

    loadData() {
        API.report
            .get(`/report/all/${this.state.reportGroup}`)
            .then(resp => {
                const { data: resData } = resp;
                const { data } = resData;
                this.setState({
                  categories: data.categories,
                  icon: data.icon,
                  loaded: true
                });
            })
            .catch(e => {
                console.error(e);
                if(localStorage.get("accessDenied401") === "true"){
                    this.setState({showLoggedoutModal:true});
                    return;
                }
                let resultCode = -1;
                if(e.data) {
                    resultCode = e.data.resultCode;
                }
                if (resultCode > -1) {
                    notifyWithMessage({type: 'error', t: this.props.t, resultCode, isNormal: true});
                } else {
                    notifyWithMessage({type: 'error', t: this.props.t, errorObject: e, isNormal: true});
                }
            });
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if(this.mainContent && this.mainContent.current) {
            this.mainContent.current.focus({ preventScroll: true });
        }
    }

  render() {
      if(this.state.sessionTerminated)
          return <Redirect to={{pathname: '/auth/logout'}}/>;

      let showLoggedOutWindow = this.state.showLoggedoutModal;

      if (!this.state.loaded && !showLoggedOutWindow) {
          return null;
      }

      let routes = mauiRoutes.getRoutes();
      const {t, navVarBrand, getActiveRoute} = this.props;

      return  (
          <div className="main-content" ref={this.mainContent} tabIndex={"-1"}>
              <Helmet>
                  <title>Knoa Analytics - {(navVarBrand) ? navVarBrand : getActiveRoute(routes, t)}</title>
              </Helmet>
              {showLoggedOutWindow && (
              <ModalDialog
                  showModal={showLoggedOutWindow}
                  modalID="mReportsLogOut"
                  title={"Log Out"}
                  contentBody={
                      <span>{getSpecificOPErrorMessages(this.props.t,"logoutfor401",null,null)}</span>
                  }
                  bodyPaddingBottom='15px'
                  footerBtnBsStyle="success"
                  footerBtnClass="KnoaSuccessButton"
                  footerBtnOnClick={this.handleLogoutConfirm}
                  footerBtnText={this.props.t('common:ok')}
              />
              )}
              <Category loaded={this.state.loaded} groupName={this.state.reportGroup} categories={this.state.categories} icon={this.state.icon} trans={this.props.t}/>
          </div>
      );
  }
}

export default withTranslation()(AllReports);